/**
 * «Importo asete» — magjistari me tre hapa.
 *
 * Regjistri i klientit ekziston para nesh: një fletë Excel me kolona të
 * shkruara për sy të tjerë. Ky ekran e pranon ashtu siç është dhe e përkthen —
 * shkarko shabllonin (nëse s'ka listë fare) → ngarko → **shih dhe rregullo
 * hartën e kolonave** → shih ç'do të ndodhë me çdo rresht → importo.
 *
 * Hapi i hartës është arsyeja pse ky ekran ekziston. Njohja automatike i gjen
 * shumicën e kolonave («Assigned to» → «Mbajtësi aktual»), por jo të gjitha,
 * dhe një hamendje e gabuar do të shkruante numrin e faturës te seria. Prandaj
 * harta shfaqet gjithmonë, edhe kur çdo kolonë u njoh — njeriu e konfirmon,
 * sistemi nuk vendos vetë.
 *
 * Gjithë logjika e leximit, e validimit dhe e shabllonit rri te
 * `utils/assetsImport.js` (e testueshme pa React). Këtu ka vetëm ekran.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, FormControlLabel, IconButton, LinearProgress,
  MenuItem, Paper, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, alpha, useTheme,
} from '@mui/material';
import CloseIcon             from '@mui/icons-material/Close';
import UploadFileIcon        from '@mui/icons-material/UploadFile';
import DownloadIcon          from '@mui/icons-material/Download';
import CheckCircleIcon       from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon      from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon      from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon         from '@mui/icons-material/ArrowBackIosNew';
import Inventory2Icon        from '@mui/icons-material/Inventory2';
import ImageOutlinedIcon     from '@mui/icons-material/ImageOutlined';
import AssignmentIndIcon     from '@mui/icons-material/AssignmentInd';

import assetsService from '../../services/assetsService';
import StatusChip    from '../common/StatusChip';
import { shrinkImage, totalMb, IMAGE_DEFAULTS } from '../../utils/imagePrep';
import {
  ALL_COLUMNS, IMPORT_COLUMNS, SECTIONS, detectMapping, unmappedHeaders, readWorkbook,
  validateRows, toPayload, summarize, downloadTemplate, downloadErrorReport, photoKey,
} from '../../utils/assetsImport';
import {
  categoryLabel, statusLabel, statusChip, conditionLabel, money,
} from '../../config/assets';

const ACCEPT = '.xlsx,.xls,.csv';
// Të njëjtat lloje që i pranon rruga e ngarkimit te serveri. Një `image/*` i
// gjerë do të lejonte HEIC-un e iPhone-it, që aty kthehet me 400.
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';
// Një import i vetëm nuk duhet të bllokojë as serverin as shfletuesin: mbi këtë
// numër rreshtash lista ndahet vetë në pjesë dhe dërgohet me radhë. Kufiri i
// serverit është 500 — ky rri mirë poshtë tij.
const CHUNK = 50;

const nz = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

export default function AssetsImportDialog({
  open, onClose, onImported, assets = [], workers = [], departments = [], company = '',
}) {
  const theme = useTheme();
  const fileRef = useRef(null);
  const photoRef = useRef(null);

  const [step, setStep] = useState('source');
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState({ headers: [], rows: [], sheetName: '', sheetNames: [] });
  const [raw, setRaw] = useState(null);          // ArrayBuffer, që fleta të ndërrohet pa ringarkuar
  const [mapping, setMapping] = useState({});
  const [readError, setReadError] = useState('');
  const [withAssignments, setWithAssignments] = useState(true);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setStep('source'); setFileName(''); setParsed({ headers: [], rows: [], sheetName: '', sheetNames: [] });
    setRaw(null); setMapping({}); setReadError(''); setBusy(false); setProgress(0); setResult(null);
    setPhotoFiles([]);
  }, []);

  const close = useCallback(() => { if (!busy) { reset(); onClose?.(); } }, [busy, reset, onClose]);

  // ── Hapi 1: leximi ────────────────────────────────────────────────────────
  const ingest = useCallback((buffer, sheet) => {
    try {
      const p = readWorkbook(buffer, sheet);
      if (!p.headers.length) {
        setReadError('Skedari nuk ka rresht me emra kolonash. Sigurohu që rreshti me titujt është i mbushur.');
        return;
      }
      setParsed(p);
      setMapping(detectMapping(p.headers));
      setReadError('');
      setStep('map');
    } catch (e) {
      setReadError('Skedari nuk u lexua dot. Pranohen .xlsx, .xls ose .csv.');
    }
  }, []);

  const onFile = useCallback(async (file) => {
    if (!file) return;
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      setRaw(buf);
      ingest(buf);
    } catch (e) {
      setReadError('Skedari nuk u lexua dot. Pranohen .xlsx, .xls ose .csv.');
    }
  }, [ingest]);

  // ── Validimi (rillogaritet sa herë ndryshon harta) ────────────────────────
  // Fotot e zgjedhura, të indeksuara me çelësin e përputhjes. `null` kur s'ka
  // asnjë — atëherë kolona e fotos rri e heshtur, sepse është opsionale.
  const photoIndex = useMemo(() => {
    if (!photoFiles.length) return null;
    const m = new Map();
    photoFiles.forEach((f) => { const k = photoKey(f.name); if (k && !m.has(k)) m.set(k, f); });
    return m;
  }, [photoFiles]);

  const rows = useMemo(() => {
    if (!parsed.rows.length) return [];
    return validateRows(parsed.rows, mapping, {
      assets, workers, departments,
      photos: photoIndex ? photoFiles.map((f) => f.name) : null,
    });
  }, [parsed.rows, mapping, assets, workers, departments, photoIndex, photoFiles]);

  const stats = useMemo(() => summarize(rows), [rows]);
  const extras = useMemo(() => unmappedHeaders(parsed.headers, mapping), [parsed.headers, mapping]);
  const missingRequired = useMemo(
    () => IMPORT_COLUMNS.filter((c) => c.required && (mapping[c.key] ?? -1) < 0),
    [mapping],
  );
  const sectioned = useMemo(
    () => SECTIONS
      .map((section) => ({ section, columns: ALL_COLUMNS.filter((c) => c.section === section.key) }))
      .filter((g) => g.columns.length),
    [],
  );

  const setColumn = (key, idx) => setMapping((m) => {
    const next = { ...m };
    // Një kolonë e skedarit s'mund t'u takojë dy fushave: zgjedhja e re e liron të vjetrën.
    if (idx > -1) Object.keys(next).forEach((k) => { if (next[k] === idx) next[k] = -1; });
    next[key] = idx;
    return next;
  });

  // ── Dërgimi ───────────────────────────────────────────────────────────────
  const runImport = useCallback(async () => {
    const ready = rows.filter((r) => r.ok).map(toPayload);
    if (!ready.length) return;
    setBusy(true); setProgress(0);
    const acc = {
      created: 0, skipped: 0, failed: 0, assigned: 0, accessories: 0,
      photos: 0, photosFailed: [], results: [],
    };
    // Rreshti → skedari i fotos. Rreshti është ura: ngarkesa e mban `row`, dhe
    // përgjigjja e serverit e kthen po atë numër bashkë me id-në e asetit.
    const photoByRow = new Map();
    if (photoIndex) {
      rows.filter((r) => r.ok && r.values.photoKey).forEach((r) => {
        const file = photoIndex.get(r.values.photoKey);
        if (file) photoByRow.set(r.index + 2, file);
      });
    }
    try {
      for (let i = 0; i < ready.length; i += CHUNK) {
        const slice = ready.slice(i, i + CHUNK);
        // eslint-disable-next-line no-await-in-loop
        const d = await assetsService.importRows(slice, { assign: withAssignments }) || {};
        acc.created += d.created || 0;
        acc.skipped += d.skipped || 0;
        acc.failed += d.failed || 0;
        acc.assigned += d.assigned || 0;
        acc.accessories += d.accessories || 0;
        acc.results.push(...(d.results || []));

        // Fotot ngarkohen menjëherë pas pjesës së vet, jo në fund: aseti sapo
        // ka marrë id, dhe një dështim rrjeti në mes nuk i lë të gjitha fotot
        // pa u ngarkuar. Dështimi këtu NUK e rrëzon importin — aseti ekziston,
        // fotoja jo, dhe kjo thuhet te rezultati.
        for (const res of (d.results || [])) {
          const file = res.status === 'created' && res.assetId ? photoByRow.get(res.row) : null;
          if (!file) continue;
          try {
            // Zvogëlohet PARA se të niset: dyqind foto drejt nga telefoni janë
            // afër një gigabajti, për fotografi që në ekran shihen 92 piksele.
            // Ruajtja mbetet e njëjta rrugë si te fotoja e profilit — ndryshon
            // vetëm sa dërgohet.
            // eslint-disable-next-line no-await-in-loop
            const small = await shrinkImage(file);
            // eslint-disable-next-line no-await-in-loop
            await assetsService.uploadFile(res.assetId, small, { kind: 'photo' });
            acc.photos++;
          } catch (err) {
            acc.photosFailed.push(file.name);
          }
        }

        setProgress(Math.round(((i + slice.length) / ready.length) * 100));
      }
      setResult(acc);
      setStep('done');
      onImported?.(acc);
    } catch (e) {
      setResult({ ...acc, error: e?.message || String(e) });
      setStep('done');
      if (acc.created) onImported?.(acc);
    } finally {
      setBusy(false);
    }
  }, [rows, withAssignments, photoIndex, onImported]);

  // ── Pjesë të ekranit ──────────────────────────────────────────────────────
  const StatPill = ({ tone, icon, value, label }) => (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 132 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: 1, display: 'grid', placeItems: 'center',
        bgcolor: alpha(theme.palette[tone].main, 0.12), color: theme.palette[tone].main,
      }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.1 }}>{value}</Typography>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'text.secondary' }}>
          {label}
        </Typography>
      </Box>
    </Paper>
  );

  // Sa rreshta e kërkojnë një foto, dhe sa prej tyre e gjetën.
  const wantPhoto = rows.filter((r) => r.ok && r.values.photoKey).length;

  const photoPicker = (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: 1, display: 'grid', placeItems: 'center', flexShrink: 0,
            bgcolor: alpha(theme.palette.text.primary, 0.06), color: 'text.secondary',
          }}>
            <ImageOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Fotot e pajisjeve <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>· opsionale</Box>
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {photoFiles.length
                ? `${photoFiles.length} skedarë · ${totalMb(photoFiles)} MB${wantPhoto ? ` · ${stats.photos} nga ${wantPhoto} rreshta u përputhën` : ''}`
                : 'Zgjidhi nga kompjuteri — përputhen me kolonën «Fotoja» dhe ngarkohen pas importit.'}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined" size="small" startIcon={<ImageOutlinedIcon />} disabled={busy}
          onClick={() => photoRef.current?.click()}
          sx={{ borderRadius: 1.5, flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
        >
          {photoFiles.length ? 'Ndrysho fotot' : 'Zgjidh fotot'}
        </Button>
        <input ref={photoRef} type="file" accept={PHOTO_ACCEPT} multiple hidden
               onChange={(e) => { setPhotoFiles([...(e.target.files || [])]); e.target.value = ''; }} />
      </Stack>
      {photoFiles.length > 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          Fotot zvogëlohen para ngarkimit (maks. {IMAGE_DEFAULTS.MAX_SIDE}px, JPG) dhe nisen një nga një —
          serveri i merr si çdo foto tjetër, në disk jashtë web-root.
        </Typography>
      )}
    </Paper>
  );

  const sourceStep = (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ '& .MuiAlert-message': { py: 0.5 } }}>
        Ngarko listën e aseteve që ke — edhe kur kolonat janë anglisht. Ato njihen vetvetiu dhe
        i konfirmon në hapin tjetër; nuk ka nevojë ta rishkruash fletën.
      </Alert>

      <Paper
        variant="outlined"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer?.files?.[0]); }}
        onClick={() => fileRef.current?.click()}
        sx={{
          p: 4, borderRadius: 2, textAlign: 'center', cursor: 'pointer', borderStyle: 'dashed',
          '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.03) },
        }}
      >
        <UploadFileIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
        <Typography sx={{ fontWeight: 700 }}>Lësho skedarin këtu ose kliko për ta zgjedhur</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>Excel (.xlsx, .xls) ose CSV</Typography>
        {fileName && <Chip size="small" label={fileName} sx={{ mt: 1.5 }} />}
      </Paper>
      <input ref={fileRef} type="file" accept={ACCEPT} hidden
             onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />

      {readError && <Alert severity="error">{readError}</Alert>}

      <Divider><Typography variant="caption" sx={{ color: 'text.secondary' }}>ose</Typography></Divider>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Nuk ke listë të gatshme?</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Shkarko shabllonin — kolonat e sakta, tre rreshta shembull, udhëzimet
            dhe listat e vlerave me emrat e departamenteve e të punonjësve që ekzistojnë te sistemi.
          </Typography>
        </Box>
        <Button
          variant="outlined" startIcon={<DownloadIcon />}
          onClick={() => downloadTemplate({ departments, workers, company })}
          sx={{ borderRadius: 1.5, flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
        >
          Shkarko shabllonin
        </Button>
      </Stack>

      {photoPicker}
    </Stack>
  );

  const mapStep = (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
        <Chip size="small" label={`${fileName}${parsed.sheetName ? ` · ${parsed.sheetName}` : ''}`} />
        <Chip size="small" color="primary" variant="outlined" label={`${parsed.rows.length} rreshta`} />
        {parsed.sheetNames.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select value={parsed.sheetName} onChange={(e) => ingest(raw, e.target.value)}
                    sx={{ height: 30, fontSize: '0.8125rem' }}>
              {parsed.sheetNames.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Stack>

      {missingRequired.length > 0 && (
        <Alert severity="warning">
          Këto kolona të detyrueshme s&apos;u lidhën ende: {missingRequired.map((c) => c.label).join(', ')}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 340 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Fusha te Nexuma</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: '38%' }}>Kolona te skedari yt</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Shembull nga rreshti i parë</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sectioned.map(({ section, columns }) => [
              // Kreu i seksionit: tridhjetë rreshta pa ndarje janë listë, jo formë.
              // Numri i lidhur përbri i tregon njeriut ku ka mbetur bosh pa i
              // lexuar të gjitha rreshtat një nga një.
              <TableRow key={`sec-${section.key}`}>
                <TableCell colSpan={3} sx={{ py: 0.5, borderBottom: 'none', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'primary.main' }}>
                    {section.label}
                    <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontWeight: 600, letterSpacing: 0 }}>
                      {columns.filter((c) => (mapping[c.key] ?? -1) > -1).length}/{columns.length}
                    </Box>
                  </Typography>
                </TableCell>
              </TableRow>,
              ...columns.map((c) => {
                const idx = mapping[c.key] ?? -1;
                const sample = idx > -1 && parsed.rows[0] ? parsed.rows[0][idx] : '';
                return (
                  <TableRow key={c.key} hover>
                    <TableCell sx={{ fontSize: '0.8125rem', pl: 2.5 }}>
                      {c.label}
                      {c.required && <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>}
                    </TableCell>
                    <TableCell>
                      <Select size="small" fullWidth value={idx}
                              onChange={(e) => setColumn(c.key, Number(e.target.value))}
                              sx={{ height: 30, fontSize: '0.8125rem' }}>
                        <MenuItem value={-1}><em>— mos e importo —</em></MenuItem>
                        {parsed.headers.map((h, i) => (
                          <MenuItem key={`${h}-${i}`} value={i}>{h || `(kolona ${i + 1})`}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {sample instanceof Date ? sample.toISOString().slice(0, 10) : nz(sample)}
                    </TableCell>
                  </TableRow>
                );
              })])}
          </TableBody>
        </Table>
      </TableContainer>

      {extras.length > 0 && (
        <Alert severity="info" icon={false} sx={{ '& .MuiAlert-message': { py: 0.5 } }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Këto kolona të skedarit nuk lidhen me asnjë fushë dhe do të anashkalohen:
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
            {extras.map((e) => <Chip key={e.index} size="small" variant="outlined" label={e.header} />)}
          </Stack>
        </Alert>
      )}
    </Stack>
  );

  const previewStep = (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.25} flexWrap="wrap" rowGap={1.25}>
        <StatPill tone="success" icon={<CheckCircleIcon sx={{ fontSize: 18 }} />} value={stats.ready} label="gati" />
        <StatPill tone="warning" icon={<WarningAmberIcon sx={{ fontSize: 18 }} />} value={stats.warned} label="me vërejtje" />
        <StatPill tone="error" icon={<ErrorOutlineIcon sx={{ fontSize: 18 }} />} value={stats.blocked} label="nuk hyjnë" />
        <StatPill tone="info" icon={<AssignmentIndIcon sx={{ fontSize: 18 }} />} value={stats.assigned} label="te punonjësit" />
        <StatPill tone="primary" icon={<Inventory2Icon sx={{ fontSize: 18 }} />} value={money(stats.value)} label="vlera e blerjes" />
      </Stack>

      <FormControlLabel
        control={<Switch size="small" checked={withAssignments} onChange={(e) => setWithAssignments(e.target.checked)} />}
        label={(
          <Typography variant="body2">
            Regjistro edhe dorëzimet te mbajtësit ({stats.assigned} rreshta) — pajisja hyn si «E caktuar», me historikun që e provon
          </Typography>
        )}
      />

      {photoPicker}

      {stats.uncategorized > 0 && (
        <Alert severity="info">
          {stats.uncategorized} rreshta vijnë pa kategori dhe do të hyjnë si «Tjetër».
          Nëse fleta jote e ka atë kolonë, lidhe te hapi i mëparshëm.
        </Alert>
      )}

      {stats.blocked > 0 && (
        <Alert severity="warning">
          {stats.blocked} rreshta kanë gabim dhe nuk do të dërgohen. Të tjerët importohen normalisht —
          pastaj shkarko raportin, rregulloji dhe ngarkoji sërish.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['#', 'Pajisja', 'Identifikimi', 'Gjendja', 'Blerja', 'Mbajtësi', 'Vërejtje'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const v = r.values;
              const issues = [...r.errors, ...r.warnings];
              const tone = !r.ok ? 'error' : r.warnings.length ? 'warning' : null;
              return (
                <TableRow key={r.index} sx={tone ? { bgcolor: alpha(theme.palette[tone].main, 0.05) } : undefined}>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{r.index + 2}</TableCell>
                  <TableCell sx={{ fontSize: '0.8125rem' }}>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{v.name || '—'}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {[categoryLabel(v.category), v.brand, v.model].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    <div>{nz(v.serialNumber)}</div>
                    <div>{nz(v.internalCode)}</div>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    <Stack spacing={0.25} alignItems="flex-start">
                      <StatusChip status={statusChip(v.status)} label={statusLabel(v.status)} size="sm" />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{conditionLabel(v.condition)}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    <div>{v.purchaseValue === null ? '—' : money(v.purchaseValue, v.currency)}</div>
                    <div>{nz(v.purchaseDate)}</div>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {/* Mbajtësi rri PRANË datës: «Dorentina» vetëm nuk thotë asgjë
                        derisa të dish që pajisja është te ajo prej marsit. */}
                    <div>{v.holderWorkerId ? v.holderText : '—'}</div>
                    <div>{v.holderWorkerId ? nz(v.assignedOn) : ''}</div>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.72rem' }}>
                    {issues.length === 0
                      ? <Typography variant="caption" sx={{ color: 'success.main' }}>Në rregull</Typography>
                      : (
                        <Stack spacing={0.25}>
                          {issues.map((e, i) => (
                            <Typography key={i} variant="caption"
                              sx={{ color: r.errors.includes(e) ? 'error.main' : 'warning.main' }}>
                              {e.message}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {busy && (
        <Box>
          <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Duke importuar… {progress}%</Typography>
        </Box>
      )}
    </Stack>
  );

  const failedRows = result?.results?.filter((r) => r.status === 'failed') || [];
  const skippedRows = result?.results?.filter((r) => r.status === 'skipped') || [];
  const doneStep = (
    <Stack spacing={2}>
      {/* Suksesi thuhet vetëm kur ka ndodhur: «përfundoi» me 0 asete të
          krijuara e bënte klientin të kërkonte te regjistri ato që s'hynë kurrë. */}
      {result?.error
        ? <Alert severity="error">Importi u ndërpre: {result.error}</Alert>
        : !result?.created && result?.failed
          ? <Alert severity="error">Asnjë aset nuk u importua — shih arsyet për çdo rresht më poshtë.</Alert>
          : result?.failed
            ? <Alert severity="warning">U importuan {result.created} asete; {result.failed} rreshta dështuan (arsyet më poshtë).</Alert>
            : <Alert severity="success">Importi përfundoi: {result?.created || 0} asete.</Alert>}

      <Stack direction="row" spacing={1.25} flexWrap="wrap" rowGap={1.25}>
        <StatPill tone="success" icon={<CheckCircleIcon sx={{ fontSize: 18 }} />} value={result?.created || 0} label="asete" />
        <StatPill tone="info" icon={<AssignmentIndIcon sx={{ fontSize: 18 }} />} value={result?.assigned || 0} label="dorëzime" />
        <StatPill tone="warning" icon={<WarningAmberIcon sx={{ fontSize: 18 }} />} value={result?.skipped || 0} label="ekzistonin" />
        <StatPill tone="error" icon={<ErrorOutlineIcon sx={{ fontSize: 18 }} />} value={result?.failed || 0} label="dështuan" />
        {!!result?.photos && (
          <StatPill tone="info" icon={<ImageOutlinedIcon sx={{ fontSize: 18 }} />} value={result.photos} label="foto" />
        )}
      </Stack>

      {result?.photosFailed?.length > 0 && (
        <Alert severity="warning">
          Asetet u krijuan, por këto foto nuk u ngarkuan: {result.photosFailed.join(', ')}.
          Shtoji nga kartela e asetit.
        </Alert>
      )}

      {(failedRows.length > 0 || skippedRows.length > 0) && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 220 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Pajisja</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Arsyeja</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...failedRows, ...skippedRows].map((r, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{r.row}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{nz(r.name)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: r.status === 'failed' ? 'error.main' : 'text.secondary' }}>
                    {r.message}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(stats.blocked > 0 || stats.warned > 0) && (
        <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ alignSelf: 'flex-start', borderRadius: 1.5, textTransform: 'none' }}
                onClick={() => downloadErrorReport(rows, parsed.headers)}>
          Shkarko rreshtat me problem
        </Button>
      )}
    </Stack>
  );

  const canNext = step === 'map' && missingRequired.length === 0 && rows.length > 0;

  return (
    <Dialog open={open} onClose={close} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2, minWidth: 0 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5, display: 'grid', placeItems: 'center', flexShrink: 0,
          bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main',
        }}>
          <Inventory2Icon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>Importo asete</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
            {step === 'source' ? 'Hapi 1 nga 3 · zgjidh skedarin'
              : step === 'map' ? 'Hapi 2 nga 3 · konfirmo kolonat'
              : step === 'preview' ? "Hapi 3 nga 3 · shiko ç'do të ndodhë"
              : 'Përfundoi'}
          </Typography>
        </Box>
        <IconButton onClick={close} size="small" disabled={busy}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ minWidth: 0 }}>
        {step === 'source' && sourceStep}
        {step === 'map' && mapStep}
        {step === 'preview' && previewStep}
        {step === 'done' && doneStep}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {(step === 'map' || step === 'preview') && (
          <Button onClick={() => setStep(step === 'map' ? 'source' : 'map')}
                  startIcon={<ArrowBackIcon sx={{ fontSize: '13px !important' }} />} disabled={busy}>
            Prapa
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {step === 'done'
          ? <Button variant="contained" onClick={close} sx={{ borderRadius: 1.5 }}>Mbyll</Button>
          : <Button onClick={close} disabled={busy}>Anulo</Button>}
        {step === 'map' && (
          <Button variant="contained" disabled={!canNext} onClick={() => setStep('preview')} sx={{ borderRadius: 1.5 }}>
            Vazhdo
          </Button>
        )}
        {step === 'preview' && (
          <Button
            variant="contained" onClick={runImport} sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 700 }}
            disabled={busy || stats.total - stats.blocked === 0}
            startIcon={busy ? <CircularProgress size={15} color="inherit" /> : <Inventory2Icon sx={{ fontSize: '17px !important' }} />}
          >
            Importo {stats.total - stats.blocked} asete
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
